import React, { useState, useEffect } from "react";
import "react-toastify/dist/ReactToastify.css";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Editor } from "@tinymce/tinymce-react";
import { validateBrand } from "../../../utils/validation";
import {
  fetchBrandData,
  addBrand,
  updateBrand,
} from "../../../services/MasterSettings/brandServices";
import allImages from "../../../assets/images-import";
import { handleErrors } from "../../../utils/errorHandler";
import { Loading } from "../../Common/OtherElements/Loading";
import { usePageLevelAccess } from "../../../hooks/usePageLevelAccess";
import { resolveFileUrl } from "../../../utils/fileUrlHelper";

const INITIAL_FORM_STATE = {
  BrandName: "",
  BrandSlug: "",
  ShortDesc: "",
  FullDesc: "",
  MetaTitle: "",
  MetaDesc: "",
  MetaKeywords: "",
  BrandImage: "",
  BrandImagePreview: "",
};

export const AddBrand = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [content, setContent] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [isSlugManuallyEdited, setIsSlugManuallyEdited] = useState(false);
  const [PageLevelAccessurl, setPageLevelAccessurl] = useState();

  const generateBrandSlug = (value) =>
    value
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9 ]/g, "")
      .replace(/\s+/g, "-");

  useEffect(() => {
    if (id) {
      setPageLevelAccessurl("/brand/update/:id");
    } else {
      setPageLevelAccessurl("brand/add");
    }
  }, [id]);

  const { pageAccessData } = usePageLevelAccess(PageLevelAccessurl);

  useEffect(() => {
    if (pageAccessData) {
      if (id) {
        if (!pageAccessData.editAccess) {
          navigate("/404-error-page");
        }
      } else {
        if (!pageAccessData.addAccess) {
          navigate("/404-error-page");
        }
      }
    } else {
      console.log("No page access details found");
    }
  });

  useEffect(() => {
    const fetchData = async () => {
      if (id) {
        setLoading(true);
        try {
          const data = await fetchBrandData(id);
          if (data) {
            setFormData({
              BrandName: data.brandName || "",
              BrandSlug: data.brandSlug || "",
              ShortDesc: data.shortDesc || "",
              FullDesc: data.fullDesc || "",
              MetaTitle: data.metaTitle || "",
              MetaDesc: data.metaDesc || "",
              MetaKeywords: data.metaKeywords || "",
              BrandImage: "",
              BrandImagePreview: resolveFileUrl(data.brandImage) || "",
            });
            setContent(data.fullDesc || "");
            setIsSlugManuallyEdited(true);
          }
        } catch (error) {
          handleErrors(error);
        } finally {
          setLoading(false);
        }
      } else {
        setFormData(INITIAL_FORM_STATE);
        setContent("");
        setIsSlugManuallyEdited(false);
      }
    };
    fetchData();
  }, [id]);

  const handleEditorChange = (value) => {
    setContent(value);
    setFormData((prevData) => ({ ...prevData, FullDesc: value }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "BrandName") {
      setFormData((prevData) => ({
        ...prevData,
        BrandName: value,
        BrandSlug: isSlugManuallyEdited
          ? prevData.BrandSlug
          : generateBrandSlug(value),
      }));
    } else if (name === "BrandSlug") {
      setIsSlugManuallyEdited(true);
      setFormData((prevData) => ({
        ...prevData,
        BrandSlug: generateBrandSlug(value),
      }));
    } else {
      setFormData((prevData) => ({ ...prevData, [name]: value }));
    }

    setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prevData) => ({
        ...prevData,
        BrandImage: file,
        BrandImagePreview: URL.createObjectURL(file),
      }));
      setErrors((prevErrors) => ({ ...prevErrors, BrandImage: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const { valid, errors: validationErrors } = validateBrand(formData);
    setErrors(validationErrors);

    if (valid) {
      setLoading(true);
      setIsButtonDisabled(true);
      try {
        if (id) {
          await updateBrand({ ...formData, id });
          navigate("/brand", {
            state: { successMessage: "Brand updated successfully!" },
          });
        } else {
          await addBrand(formData);
          navigate("/brand", {
            state: { successMessage: "Brand added successfully!" },
          });
        }
      } catch (error) {
        handleErrors(error);
      } finally {
        setLoading(false);
        setIsButtonDisabled(false);
      }
    }
  };

  return (
    <>
      <div className="row">
        <div className="col-12">
          <div className="page-title-box d-sm-flex align-items-center justify-content-between">
            <h4 className="mb-sm-0">{id ? "Update Brand" : "Add Brand"}</h4>
            <div className="page-title-right">
              <ol className="breadcrumb m-0">
                <li className="breadcrumb-item">
                  <Link to="/">
                    <i className="ri-home-2-fill"></i>
                  </Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/brand">Manage Brands</Link>
                </li>
                <li className="breadcrumb-item">
                  {id ? `Update Brand-${id}` : "Add Brand"}
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <div className="card-body p-2">
        <form onSubmit={handleSubmit} method="POST">
          <div className="row">
            <div className="col-lg-8">
              <div className="card mt-xxl-n5 p-3">
                <div className="card-header-wrapper p-1">
                  <h5 className="blogs-heading">Brand Details</h5>
                </div>
                <div className="blog-name-wrapper mt-3">
                  <div className="mb-3 col-lg-6">
                    <label className="form-label">
                      Brand Name <span className="required-field">*</span>
                    </label>
                    <input
                      type="text"
                      name="BrandName"
                      value={formData.BrandName}
                      placeholder="Enter Brand Name"
                      onChange={handleInputChange}
                      className={`form-control ${
                        errors.BrandName ? "is-invalid" : ""
                      }`}
                    />
                    {errors.BrandName && (
                      <div className="invalid-feedback">
                        {errors.BrandName}
                      </div>
                    )}
                  </div>

                  <div className="mb-3 col-lg-6">
                    <label className="form-label">
                      Brand Slug <span className="required-field">*</span>
                    </label>
                    <input
                      type="text"
                      name="BrandSlug"
                      value={formData.BrandSlug}
                      placeholder="Enter Brand Slug"
                      onChange={handleInputChange}
                      className={`form-control ${
                        errors.BrandSlug ? "is-invalid" : ""
                      }`}
                    />
                    {errors.BrandSlug && (
                      <div className="invalid-feedback">
                        {errors.BrandSlug}
                      </div>
                    )}
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">Short Description</label>
                  <textarea
                    name="ShortDesc"
                    value={formData.ShortDesc}
                    placeholder="Enter Short Description"
                    onChange={handleInputChange}
                    className={`form-control ${
                      errors.ShortDesc ? "is-invalid" : ""
                    }`}
                    rows="3"
                  ></textarea>
                  {errors.ShortDesc && (
                    <div className="invalid-feedback">
                      {errors.ShortDesc}
                    </div>
                  )}
                </div>

                <div className="mb-3">
                  <label className="form-label">Full Description</label>
                  <Editor
                    tinymceScriptSrc="/tinymce/tinymce.min.js"
                    value={content}
                    init={{
                      height: 400,
                      menubar: false,
                      plugins: [
                        "advlist",
                        "autolink",
                        "link",
                        "image",
                        "lists",
                        "charmap",
                        "preview",
                        "anchor",
                        "pagebreak",
                        "searchreplace",
                        "wordcount",
                        "visualblocks",
                        "code",
                        "fullscreen",
                        "insertdatetime",
                        "media",
                        "table",
                        "emoticons",
                        "template",
                        "help",
                      ],
                      toolbar:
                        "undo redo | styles | bold italic | forecolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link image | preview media fullscreen | emoticons | help",
                    }}
                    onEditorChange={handleEditorChange}
                  />
                </div>
              </div>
            </div>

            <div className="col-lg-4">
              <div className="card mt-xxl-n5 p-3">
                <div className="card-header-wrapper">
                  <h5 className="">Brand Image</h5>
                </div>
                <div className="mt-3 d-flex justify-content-center">
                  <div className="profile-user position-relative d-inline-block mx-auto mb-4">
                    <img
                      src={
                        formData.BrandImagePreview
                          ? resolveFileUrl(formData.BrandImagePreview)
                          : allImages.DefultImage
                      }
                      className="rounded-circle avatar-xl img-thumbnail user-profile-image shadow"
                      alt="Brand Preview"
                    />
                    <div className="avatar-xs p-0 rounded-circle profile-photo-edit">
                      <input
                        id="brandImage"
                        type="file"
                        accept="image/*"
                        className="profile-img-file-input"
                        onChange={handleImageChange}
                      />
                      <label
                        htmlFor="brandImage"
                        className="profile-photo-edit avatar-xs"
                      >
                        <span className="avatar-title rounded-circle bg-light text-body shadow">
                          <i className="ri-camera-fill"></i>
                        </span>
                      </label>
                    </div>
                    {errors.BrandImage && (
                      <div className="invalid-feedback d-block">
                        {errors.BrandImage}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="card mt-xxl-n5 p-3">
                <div className="card-header-wrapper">
                  <h5 className="">Meta Data</h5>
                </div>
                <div className="mt-3">
                  <label className="form-label">Meta Title</label>
                  <input
                    type="text"
                    name="MetaTitle"
                    value={formData.MetaTitle}
                    placeholder="Enter Meta Title"
                    onChange={handleInputChange}
                    className="form-control"
                  />
                </div>
                <div className="mt-2">
                  <label className="form-label">Meta Keywords</label>
                  <input
                    type="text"
                    name="MetaKeywords"
                    value={formData.MetaKeywords}
                    placeholder="Enter Meta Keywords"
                    onChange={handleInputChange}
                    className="form-control"
                  />
                </div>
                <div className="mt-2">
                  <label className="form-label">Meta Description</label>
                  <textarea
                    name="MetaDesc"
                    value={formData.MetaDesc}
                    placeholder="Enter Meta Description"
                    onChange={handleInputChange}
                    className="form-control"
                    rows="3"
                  ></textarea>
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-12">
            <div className="pt-4">
              <button
                type="submit"
                className="btn btn-secondary"
                disabled={isButtonDisabled}
              >
                {isButtonDisabled
                  ? id
                    ? "Updating"
                    : "Saving"
                  : id
                  ? "Update"
                  : "Save"}
              </button>
              <Link to="/brand" className="btn btn-danger ms-1">
                Cancel
              </Link>
            </div>
          </div>
          {loading && <Loading />}
        </form>
      </div>
    </>
  );
};